import React, { useEffect, useState } from "react";
import { PartnerLayout } from "./PartnerLayout";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { StatusBadge } from "../admin/shared";
import partnerApi from "../../lib/partnerApi";
import { toast } from "sonner";
import { Send, FlaskConical } from "lucide-react";

export default function PartnerSandbox() {
  const [partner, setPartner] = useState(null);
  const [event, setEvent] = useState("order.completed");
  const [firing, setFiring] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    partnerApi.get("/partner/me").then((r) => setPartner(r.data));
    partnerApi.get("/partner/webhook-logs").then((r) => setLogs(r.data.logs || []));
  }, []);

  const toggleSandbox = async () => {
    const next = !partner.sandbox_mode;
    try {
      await partnerApi.patch("/partner/settings", { sandbox_mode: next });
      setPartner({ ...partner, sandbox_mode: next });
      toast.success(`Sandbox ${next ? "enabled" : "disabled"}`);
    } catch { toast.error("Failed"); }
  };

  const fireTest = async () => {
    setFiring(true); setLastResult(null);
    try {
      const r = await partnerApi.post("/partner/test-webhook", { event });
      setLastResult(r.data);
      if (r.data.delivered) toast.success("Webhook delivered");
      else toast.error("Webhook failed");
      const refresh = await partnerApi.get("/partner/webhook-logs");
      setLogs(refresh.data.logs || []);
    } catch (e) { toast.error("Failed"); }
    finally { setFiring(false); }
  };

  if (!partner) return <PartnerLayout title="Sandbox"><div className="text-sm text-muted-foreground">Loading…</div></PartnerLayout>;

  return (
    <PartnerLayout title="Sandbox" subtitle="Test mode + webhook firing + full delivery log">
      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <div className="xch-card p-5" data-testid="sandbox-mode-card">
          <div className="flex items-center gap-2 mb-2">
            <FlaskConical className="w-4 h-4 text-primary" />
            <div className="font-display text-base font-bold text-foreground">Sandbox mode</div>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            When ON, all orders go through the mocked state machine — no real payments are processed.
          </p>
          <div className="flex items-center justify-between bg-muted rounded-xl p-3">
            <span className="text-sm font-semibold">{partner.sandbox_mode ? "Sandbox enabled" : "Live mode"}</span>
            <Switch checked={partner.sandbox_mode} onCheckedChange={toggleSandbox} data-testid="sandbox-toggle" />
          </div>
        </div>

        <div className="xch-card p-5" data-testid="fire-webhook-card">
          <div className="flex items-center gap-2 mb-2">
            <Send className="w-4 h-4 text-primary" />
            <div className="font-display text-base font-bold text-foreground">Fire test webhook</div>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Sends a synthetic event to your webhook URL: <span className="font-mono">{partner.webhook_url || "not configured"}</span></p>
          <div className="flex gap-2">
            <Select value={event} onValueChange={setEvent}>
              <SelectTrigger className="rounded-xl" data-testid="event-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="order.completed">order.completed</SelectItem>
                <SelectItem value="order.failed">order.failed</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fireTest} disabled={firing || !partner.webhook_url} className="bg-primary hover:bg-primary/90 text-white rounded-xl" data-testid="fire-webhook-btn">
              {firing ? "Sending…" : "Fire"}
            </Button>
          </div>
          {lastResult && (
            <div className={`mt-3 text-xs rounded-xl p-3 ${lastResult.delivered ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`} data-testid="fire-result">
              <div className="font-semibold mb-1">HTTP {lastResult.status || "—"} · {lastResult.latency_ms || "?"}ms · {lastResult.attempts} attempts</div>
              {lastResult.response_body && <pre className="font-mono text-[10px] bg-white/60 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-32">{lastResult.response_body}</pre>}
            </div>
          )}
        </div>
      </div>

      <div className="xch-card overflow-hidden" data-testid="webhook-log-table">
        <div className="p-4 border-b border-border">
          <div className="font-display text-base font-bold text-foreground">Webhook delivery log</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-muted/50 border-b border-border">
              <Th>When</Th><Th>Event</Th><Th>Order</Th><Th right>HTTP</Th><Th right>Attempts</Th><Th>Status</Th><Th>Response</Th>
            </tr></thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground text-sm">No webhook attempts yet. Fire one above.</td></tr>}
              {logs.map((w) => (
                <tr key={w.id} className="border-b border-border last:border-0" data-testid={`webhook-log-${w.id}`}>
                  <td className="px-3 py-2 text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">{new Date(w.last_attempt_at).toLocaleString("en-IE")}</td>
                  <td className="px-3 py-2 text-xs font-mono">{w.event || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{(w.order_id || "—").slice(0, 12)}{w.order_id && w.order_id.length > 12 ? "…" : ""}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono">{w.last_status || "—"}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono">{w.attempts}</td>
                  <td className="px-3 py-2"><StatusBadge status={w.status} /></td>
                  <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground max-w-[260px] truncate" title={w.last_response_body}>{w.last_response_body || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PartnerLayout>
  );
}

const Th = ({ children, right }) => (
  <th className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground ${right ? "text-right" : "text-left"}`}>{children}</th>
);
